// Wallet Ops → Job health — the chain-scan pipeline dashboard
// (SM-211). One card per network from crypto-wallet-chainnode's
// /admin/pipeline/status: cursor vs chain head, lag, lease owner,
// per-state block-job counts, and the last error. Auto-refreshes
// every 5s so an operator can watch a stuck cursor recover.
//
// Operators with CRYPTO_WALLET_MANUAL_OP also get the safe replay /
// reconcile controls (SM-254): pause a network's scanner, override its
// scanned-block cursor, resume it, and requeue failed block jobs. Every
// action requires an audit reason and is gated + logged server-side.
// The cursor override is deliberately gated on the scanner being PAUSED
// first (the chainnode refuses it otherwise) — latest_block stays
// read-only because it is observed from the chain RPC, not set by hand.
//
// Chain reorgs recover themselves (SM-533): a network walks RUNNING →
// REORG_DETECTED → RECOVERING → RUNNING with no operator action, and this
// page is where that is visible. The one state that DOES need a human is
// PAUSED_DEEP_REORG — a reorg past the configured depth, or one that would
// invalidate a credit already paid out — and there the override stops being
// a routine tool and becomes break-glass, which is why it is enabled from
// that state too.
import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { ConfirmModal } from "@/components/ui/ConfirmModal";
import { Input } from "@/components/ui/Input";
import { Skeleton } from "@/components/ui/Skeleton";
import { useMyPerms } from "@/hooks/useMyPerms";
import { Perm } from "@/lib/perms";
import { useNetworkMap, networkLabel } from "@/components/wallet-ops/orders";
import {
  cryptoPipelineOp,
  fetchPipelineStatus,
  walletKeys,
  type PipelineOp,
} from "@/lib/wallet-queries";
import type { PipelineNetworkHealth, PipelineReorg } from "@/lib/wallet-types";
import { humanizeError } from "@/lib/errorMessages";
import { toast } from "@/lib/toast";

export const Route = createFileRoute("/_app/wallet-ops/health")({
  component: JobHealthPage,
});

const POLL_MS = 5_000;

function lagTone(lag: number | undefined): "success" | "warning" | "danger" {
  if (lag === undefined) return "warning";
  if (lag <= 3) return "success";
  if (lag <= 25) return "warning";
  return "danger";
}

function statusTone(s: string | undefined): "success" | "warning" | "danger" | "muted" {
  switch (s) {
    case "OK":
    case "RUNNING":
    case "HEALTHY":
      return "success";
    case "PAUSED":
    case "LAGGING":
    case "DEGRADED":
    // Working states of an automatic reorg recovery: the scanner is busy,
    // not broken, so they read as warnings rather than failures.
    case "REORG_DETECTED":
    case "RECOVERING":
      return "warning";
    case "STALLED":
    case "ERROR":
    // Fail-closed: this network is stopped until an operator reconciles it.
    case "PAUSED_DEEP_REORG":
      return "danger";
    default:
      return "muted";
  }
}

function num(v: number | undefined): string {
  return v === undefined ? "—" : v.toLocaleString();
}

// The four reconcile operations an operator can drive from a card.
type OpKind = "pause" | "resume" | "retry" | "override";

interface ActiveOp {
  kind: OpKind;
  network: PipelineNetworkHealth;
}

const OP_SUCCESS: Record<OpKind, string> = {
  pause: "Scanner paused.",
  resume: "Scanner resumed.",
  retry: "Failed block jobs requeued.",
  override: "Scanned block overridden.",
};

// Wire mapping: the modal kind → the chainnode op path.
const OP_PATH: Record<Exclude<OpKind, "override">, PipelineOp> = {
  pause: "pause",
  resume: "resume",
  retry: "retry-failed",
};

function JobHealthPage() {
  const qc = useQueryClient();
  const perms = useMyPerms();
  const nets = useNetworkMap();
  // While perms load, optimistically show the controls — the server
  // still rejects unauthorized ops (same posture as the nav / config).
  const canManage =
    !perms.loaded || perms.isSuperAdmin || perms.has(Perm.CryptoWalletManualOp);

  const status = useQuery({
    queryKey: walletKeys.pipeline(),
    queryFn: () => fetchPipelineStatus(),
    refetchInterval: POLL_MS,
  });

  const [active, setActive] = useState<ActiveOp | null>(null);
  const [targetBlock, setTargetBlock] = useState("");

  const runOp = useMutation({
    mutationFn: (v: {
      kind: OpKind;
      op: PipelineOp;
      body: Record<string, unknown>;
      reason: string;
    }) => cryptoPipelineOp(v.op, v.body, v.reason),
    onSuccess: async (_data, v) => {
      toast.success(OP_SUCCESS[v.kind]);
      setActive(null);
      setTargetBlock("");
      await qc.invalidateQueries({ queryKey: walletKeys.pipeline() });
    },
  });

  // Open a modal for one op. Prefill the override input with the current
  // cursor so the operator edits from a known value, and clear any error
  // left over from a previous attempt.
  const startOp = (kind: OpKind, network: PipelineNetworkHealth) => {
    runOp.reset();
    setTargetBlock(
      kind === "override" ? String(network.current_scanned_block ?? "") : "",
    );
    setActive({ kind, network });
  };

  const closeOp = () => {
    if (runOp.isPending) return;
    setActive(null);
    setTargetBlock("");
  };

  const blockValid =
    /^\d+$/.test(targetBlock.trim()) &&
    Number.isSafeInteger(Number(targetBlock.trim()));
  const modalError = runOp.isError ? humanizeError(runOp.error) : null;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl tracking-tight">Job health</h1>
          <p className="text-sm text-muted">
            Chain-scan pipeline per network: cursor position, chain head, lag,
            lease owner, and block-job counts. Refreshes every {POLL_MS / 1000}s.
          </p>
        </div>
        {status.data && (
          <Badge tone="chrome">role: {status.data.role || "unknown"}</Badge>
        )}
      </div>

      {status.isLoading && (
        <div className="space-y-5">
          <Skeleton className="h-52" />
        </div>
      )}
      {status.isError && (
        <Card>
          <CardBody>
            <p className="py-8 text-center text-sm text-no">
              {(status.error as Error).message}
            </p>
          </CardBody>
        </Card>
      )}
      {status.data && status.data.networks.length === 0 && (
        <Card>
          <CardBody>
            <p className="py-8 text-center text-sm text-muted">
              The chainnode reports no configured networks.
            </p>
          </CardBody>
        </Card>
      )}
      {status.data && status.data.networks.length > 0 && (
        <div className="space-y-5">
          {status.data.networks.map((n) => (
            <NetworkCard
              key={n.network_id}
              health={n}
              canManage={canManage}
              onOp={(kind) => startOp(kind, n)}
            />
          ))}
        </div>
      )}

      {/* Pause / resume / retry-failed — reason-only confirmation. */}
      {active && active.kind !== "override" && (
        <ConfirmModal
          open
          tone={active.kind === "pause" ? "danger" : "primary"}
          title={confirmCopy(active.kind, networkLabel(nets, active.network.network_id)).title}
          description={confirmCopy(active.kind, networkLabel(nets, active.network.network_id)).body}
          confirmLabel={confirmCopy(active.kind, networkLabel(nets, active.network.network_id)).confirmLabel}
          cancelLabel="Cancel"
          reasonLabel="Reason (audit log)"
          reasonRequired
          loading={runOp.isPending}
          error={modalError}
          onConfirm={(reason) =>
            runOp.mutate({
              kind: active.kind,
              op: OP_PATH[active.kind as Exclude<OpKind, "override">],
              body: { network_id: active.network.network_id },
              reason,
            })
          }
          onCancel={closeOp}
        />
      )}

      {/* Cursor override — target-block field embedded in the reason
          modal, blocked until the block parses. */}
      {active?.kind === "override" && (
        <ConfirmModal
          open
          tone="danger"
          title="Override scanned block"
          description={
            <OverrideBody
              network={active.network}
              value={targetBlock}
              onChange={setTargetBlock}
              valid={blockValid}
            />
          }
          confirmLabel="Override cursor"
          cancelLabel="Cancel"
          reasonLabel="Reason (audit log)"
          reasonRequired
          confirmDisabled={!blockValid}
          loading={runOp.isPending}
          error={modalError}
          onConfirm={(reason) =>
            runOp.mutate({
              kind: "override",
              op: "cursor-override",
              body: {
                network_id: active.network.network_id,
                block: Number(targetBlock.trim()),
              },
              reason,
            })
          }
          onCancel={closeOp}
        />
      )}
    </div>
  );
}

// Copy for the reason-only confirmations (pause / resume / retry).
function confirmCopy(
  kind: Exclude<OpKind, "override"> | OpKind,
  net: string,
): { title: string; body: React.ReactNode; confirmLabel: string } {
  switch (kind) {
    case "pause":
      return {
        title: `Pause the ${net} scanner?`,
        confirmLabel: "Pause scanner",
        body: (
          <p>
            New block jobs stop being scheduled for <strong>{net}</strong>.
            In-flight jobs finish and nothing is credited past the current
            cursor. Pausing is required before overriding the cursor.
          </p>
        ),
      };
    case "resume":
      return {
        title: `Resume the ${net} scanner?`,
        confirmLabel: "Resume scanner",
        body: (
          <p>
            The coordinator resumes scheduling block jobs for{" "}
            <strong>{net}</strong> from the current cursor.
          </p>
        ),
      };
    default: // retry
      return {
        title: `Requeue failed block jobs for ${net}?`,
        confirmLabel: "Retry failed",
        body: (
          <p>
            Every failed block job on <strong>{net}</strong> is requeued
            regardless of attempt count and re-runs through the idempotent
            pipeline. Already-credited deposits are protected by their event
            keys.
          </p>
        ),
      };
  }
}

// Body of the cursor-override modal: read-only context figures + the
// editable target-block field. Rendered inside ConfirmModal's description
// slot; the reason textarea below it is the modal's own.
function OverrideBody({
  network: h,
  value,
  onChange,
  valid,
}: {
  network: PipelineNetworkHealth;
  value: string;
  onChange: (v: string) => void;
  valid: boolean;
}) {
  const nets = useNetworkMap();
  const target = /^\d+$/.test(value.trim()) ? Number(value.trim()) : undefined;
  const aboveHead =
    target !== undefined &&
    h.latest_block !== undefined &&
    target > h.latest_block;
  return (
    <div className="space-y-3">
      <p>
        Directly set the scanned cursor for <strong>{networkLabel(nets, h.network_id)}</strong>.
        Blocks between the new cursor and the chain head are re-scanned;
        deposits already credited are protected by idempotent event keys.
        The cursor generation is bumped so any in-flight commit from the old
        position is rejected.
      </p>
      <div className="flex gap-8 rounded-md border border-border bg-surface-2 px-3 py-2.5">
        <div>
          <div className="text-2xs uppercase tracking-wide text-muted">
            Current scanned
          </div>
          <div className="num text-sm text-fg">{num(h.current_scanned_block)}</div>
        </div>
        <div>
          <div className="text-2xs uppercase tracking-wide text-muted">
            Latest block
          </div>
          <div className="num text-sm text-fg">{num(h.latest_block)}</div>
        </div>
      </div>
      <Input
        label="New scanned block"
        inputMode="numeric"
        value={value}
        onChange={(e) => onChange(e.target.value.replace(/[^\d]/g, ""))}
        placeholder="block number"
        error={
          value.trim() !== "" && !valid
            ? "Enter a whole block number."
            : undefined
        }
        hint={
          aboveHead
            ? "This is above the observed chain head — the scanner will idle until the chain reaches it."
            : undefined
        }
      />
    </div>
  );
}

function NetworkCard({
  health: h,
  canManage,
  onOp,
}: {
  health: PipelineNetworkHealth;
  canManage: boolean;
  onOp: (kind: OpKind) => void;
}) {
  const nets = useNetworkMap();
  if (h.error) {
    return (
      <Card>
        <CardHeader className="border-b border-border">
          <CardTitle title={h.network_id}>{networkLabel(nets, h.network_id)}</CardTitle>
          <Badge tone="danger">UNAVAILABLE</Badge>
        </CardHeader>
        <CardBody>
          <p className="text-sm text-no">{h.error}</p>
        </CardBody>
      </Card>
    );
  }
  // Both halted states unlock the override: PAUSED is the operator's own
  // pause, PAUSED_DEEP_REORG is the fail-closed the recovery handed over.
  const paused = h.status === "PAUSED" || h.status === "PAUSED_DEEP_REORG";
  const failed = h.failed ?? 0;
  const reorg = h.reorg;
  // A network disabled/archived in Configuration → Networks: the coordinator
  // halts its scanner but leaves status RUNNING, and it stops polling the
  // chain head — so latest_block and lag freeze at their last live values and
  // must not be read as current. The DISABLED badge keys off `enabled`, and
  // the stale figures are greyed with an explanatory hint.
  const disabled = h.enabled === false;
  return (
    <Card>
      <CardHeader className="border-b border-border">
        <CardTitle title={h.network_id}>{networkLabel(nets, h.network_id)}</CardTitle>
        <span className="flex items-center gap-1.5">
          {disabled ? (
            <Badge tone="muted">DISABLED</Badge>
          ) : (
            <>
              {h.scheduling_halted && <Badge tone="danger">HALTED</Badge>}
              <Badge tone={lagTone(h.lag)}>lag {num(h.lag)}</Badge>
              <Badge tone={statusTone(h.status)}>{h.status ?? "UNKNOWN"}</Badge>
            </>
          )}
        </span>
      </CardHeader>
      <CardBody className="space-y-6">
        {disabled && (
          <p className="rounded-md border border-border bg-surface-2 px-3 py-2 text-2xs text-muted">
            Disabled in Configuration → Networks. The scanner is halted, so the
            latest-block and lag below are the last values seen while live —
            not the current chain head. Re-enable the network to resume.
          </p>
        )}
        {/* Cursor vs. chain head — the figures an operator reads first, as an
            open band that spreads across the card on desktop. */}
        <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
          <dl className="flex flex-wrap gap-x-10 gap-y-4">
            <Figure label="Scanned block" value={num(h.current_scanned_block)} />
            <Figure label="Latest block" value={num(h.latest_block)} stale={disabled} />
            <Figure
              label="Lag"
              value={num(h.lag)}
              tone={disabled ? undefined : lagTone(h.lag)}
              stale={disabled}
            />
            <Figure label="Cursor generation" value={num(h.cursor_generation)} />
          </dl>
          <div className="min-w-0 lg:max-w-xs lg:text-right">
            <div className="text-2xs uppercase tracking-wide text-muted">Lease owner</div>
            <div className="truncate font-mono text-xs text-fg" title={h.lease_owner || undefined}>
              {h.lease_owner || "—"}
            </div>
          </div>
        </div>

        <div>
          <div className="mb-2 text-2xs uppercase tracking-wide text-muted">Block jobs</div>
          <div className="grid grid-cols-3 gap-2.5 sm:grid-cols-6">
            <Count label="Queued" value={h.queued} />
            <Count label="Processing" value={h.processing} />
            <Count label="Succeeded" value={h.succeeded} />
            <Count label="Pending prev" value={h.pending_previous} danger={h.scheduling_halted} />
            <Count label="Failed" value={h.failed} danger />
            <Count label="Committed" value={h.committed} />
          </div>
        </div>

        <ReorgPanel reorg={reorg} status={h.status} networkError={h.last_error} />

        {h.last_error && (
          <p className="rounded-md border border-no/30 bg-no/5 px-3 py-2 text-2xs text-no">
            {h.last_error}
          </p>
        )}

        {/* Reconcile controls (SM-254) — CRYPTO_WALLET_MANUAL_OP. */}
        {canManage && (
          <div className="flex flex-wrap items-center gap-2 border-t border-border pt-4">
            {paused ? (
              <Button size="sm" variant="primary" onClick={() => onOp("resume")}>
                Resume scanner
              </Button>
            ) : (
              <Button size="sm" variant="ghost" onClick={() => onOp("pause")}>
                Pause scanner
              </Button>
            )}
            <Button
              size="sm"
              variant="ghost"
              disabled={!paused}
              title={paused ? undefined : "Pause the scanner first"}
              onClick={() => onOp("override")}
            >
              Override scanned block
            </Button>
            {failed > 0 && (
              <Button size="sm" variant="ghost" onClick={() => onOp("retry")}>
                Retry {failed.toLocaleString()} failed
              </Button>
            )}
            <div className="flex-1" />
            {!paused && (
              <span className="text-2xs text-muted">
                Pause to override the cursor. Ordinary reorgs recover on their
                own — no override needed.
              </span>
            )}
          </div>
        )}
      </CardBody>
    </Card>
  );
}

// ReorgPanel is the reorg surface of one network (SM-533). It renders only
// once the network has actually reorged: on a chain that never has, an
// always-present empty panel would be noise on every card.
function ReorgPanel({
  reorg,
  status,
  networkError,
}: {
  reorg: PipelineReorg | undefined;
  status: string | undefined;
  // The cursor's last_error, which the card renders below the panel. While a
  // network is failed closed the two carry the same sentence, so the panel
  // stays quiet rather than printing it twice.
  networkError: string | undefined;
}) {
  const inc = reorg?.latest;
  if (!reorg || (!inc && !(reorg.incidents ?? 0))) return null;
  const deep = status === "PAUSED_DEEP_REORG";
  const live = deep || status === "RECOVERING" || status === "REORG_DETECTED";
  return (
    <div
      className={`rounded-md border px-3 py-2.5 ${
        deep ? "border-no/40 bg-no/5" : live ? "border-warn/40 bg-warn/5" : "border-border bg-surface-2"
      }`}
    >
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <span className="text-2xs uppercase tracking-wide text-muted">Chain reorgs</span>
        <Badge tone={deep ? "danger" : live ? "warning" : "muted"}>
          {inc?.status ?? "NONE"}
        </Badge>
        <span className="flex-1" />
        <dl className="flex flex-wrap gap-x-6 gap-y-1">
          <Figure label="Reorgs" value={num(reorg.incidents)} />
          <Figure label="Deepest" value={num(reorg.max_depth_seen)} />
          <Figure label="Depth limit" value={num(reorg.max_depth_limit)} />
        </dl>
      </div>
      {deep && (
        <p className="mb-2 text-2xs text-no">
          Recovery stopped on purpose: this reorg is past the safe depth, or it
          would invalidate a deposit already credited. Nothing was skipped and
          no balance was changed — reconcile, then use the override below to
          set the cursor and resume. Other networks are unaffected.
        </p>
      )}
      {inc && (
        <dl className="grid grid-cols-2 gap-x-8 gap-y-2 sm:grid-cols-4">
          <Figure label="Conflict block" value={num(inc.conflict_block)} />
          <Figure label="Common ancestor" value={num(inc.common_ancestor)} />
          <Figure label="Depth" value={num(inc.depth)} tone={deep ? "danger" : undefined} />
          <Figure
            label="Rolled back"
            value={
              inc.rollback_from && inc.rollback_to
                ? `${inc.rollback_from.toLocaleString()}–${inc.rollback_to.toLocaleString()}`
                : "—"
            }
          />
          <Figure label="Orphaned events" value={num(inc.orphaned_events)} />
          <Figure label="Discarded jobs" value={num(inc.discarded_jobs)} />
          <Figure
            label="Finality violations"
            value={num(inc.finality_violations)}
            tone={(inc.finality_violations ?? 0) > 0 ? "danger" : undefined}
          />
          <Figure
            label="Recovery"
            value={inc.recovery_seconds ? `${inc.recovery_seconds.toFixed(1)}s` : "—"}
          />
        </dl>
      )}
      {inc && (inc.observed_hash || inc.canonical_hash) && (
        <dl className="mt-3 space-y-1.5">
          <HashRow label="Committed hash" value={inc.observed_hash} />
          <HashRow label="Canonical hash" value={inc.canonical_hash} />
        </dl>
      )}
      {inc?.last_error && inc.last_error !== networkError && (
        <p className="mt-2 text-2xs text-no">{inc.last_error}</p>
      )}
    </div>
  );
}

// HashRow labels a block hash instead of leaning on an inline word, so the
// pair reads as data (and each label is a whole phrase for the i18n catalog).
function HashRow({ label, value }: { label: string; value: string | undefined }) {
  return (
    <div className="flex min-w-0 items-baseline gap-2">
      <dt className="shrink-0 text-2xs uppercase tracking-wide text-muted">{label}</dt>
      <dd className="truncate font-mono text-2xs text-fg" title={value || undefined}>
        {value || "—"}
      </dd>
    </div>
  );
}

function Figure({
  label,
  value,
  tone,
  stale,
}: {
  label: string;
  value: string;
  tone?: "success" | "warning" | "danger";
  // A frozen value that no longer tracks live state (a disabled network's
  // latest_block/lag): rendered muted so it doesn't read as current.
  stale?: boolean;
}) {
  const toneCls = stale
    ? "text-muted"
    : tone === "danger"
      ? "text-no"
      : tone === "warning"
        ? "text-warn"
        : "text-fg";
  return (
    <div>
      <dt className="text-2xs uppercase tracking-wide text-muted">{label}</dt>
      <dd className={`num mt-0.5 text-2xl leading-none ${toneCls}`}>{value}</dd>
    </div>
  );
}

function Count({
  label,
  value,
  danger,
}: {
  label: string;
  value: number | undefined;
  danger?: boolean;
}) {
  const hot = danger && (value ?? 0) > 0;
  return (
    <div
      className={`rounded-lg border px-3 py-2.5 text-center ${
        hot ? "border-no/40 bg-no/10" : "border-border bg-surface-2"
      }`}
    >
      <div className={`num text-lg font-semibold leading-tight ${hot ? "text-no" : "text-fg"}`}>
        {num(value)}
      </div>
      <div className="mt-0.5 text-2xs uppercase tracking-wide text-muted">{label}</div>
    </div>
  );
}
